import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import api from './axiosConfig';

const universidades = [
  { sigla: 'ESPE', nombre: 'Universidad de las Fuerzas Armadas', logo: '/images/espe.webp', url: 'https://www.espe.edu.ec' },
  { sigla: 'EPN', nombre: 'Escuela Politécnica Nacional', logo: '/images/epn.webp', url: 'https://www.epn.edu.ec' },
  { sigla: 'UTMACH', nombre: 'Universidad Técnica de Machala', logo: '/images/utmach.webp', url: 'https://www.utmachala.edu.ec/portalwp/' },
  { sigla: 'UPEC', nombre: 'Universidad Politécnica Estatal del Carchi', logo: '/images/UPEC.webp', url: 'https://upec.edu.ec' },
];

function AnimatedNumber({ value, durationMs = 2800, prefix = '', suffix = '' }) {
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    const start = performance.now();
    const from = 0;
    const to = Number(value) || 0;

    let raf = 0;
    const tick = (now) => {
      const t = Math.min(1, (now - start) / durationMs);
      const eased = 1 - Math.pow(1 - t, 4); // easeOutQuart (más suave y lento)
      const current = Math.round(from + (to - from) * eased);
      setDisplay(current);
      if (t < 1) raf = requestAnimationFrame(tick);
    };

    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [value, durationMs]);

  return (
    <span>
      {prefix}
      {display.toLocaleString('es-EC')}
      {suffix}
    </span>
  );
}

function Inicio() {
  const navigate = useNavigate();
  const [modalOpen, setModalOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const stripRef = useRef(null);
  const [kpis, setKpis] = useState({
    investigadores: 0,
    publicaciones: 0,
    articulos_q1: 0,
  });

  const openCarouselAt = (idx) => {
    setActiveIndex(idx);
    setModalOpen(true);
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/api/inicio/kpis');
        if (!cancelled && res?.data) {
          setKpis({
            investigadores: Number(res.data.investigadores) || 0,
            publicaciones: Number(res.data.publicaciones) || 0,
            articulos_q1: Number(res.data.articulos_q1) || 0,
          });
        }
      } catch (error) {
        console.error('No se pudieron cargar los KPIs del inicio:', error);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!modalOpen) return;
    const el = stripRef.current;
    if (!el) return;
    const child = el.children?.[activeIndex];
    if (child?.scrollIntoView) child.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
  }, [modalOpen, activeIndex]);

  useEffect(() => {
    if (!modalOpen) return;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setModalOpen(false);
      if (e.key === 'ArrowLeft') setActiveIndex((i) => Math.max(0, i - 1));
      if (e.key === 'ArrowRight') setActiveIndex((i) => Math.min(universidades.length - 1, i + 1));
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [modalOpen]);

  return (
    <div className="min-h-[calc(100vh-64px)] flex flex-col bg-white dark:bg-slate-950 text-slate-800 dark:text-slate-200 cedia-background">
      {/* Hero */}
      <main className="relative z-10 flex-1">
        <div className="diagonal-accent hidden lg:block" />

        <section className="relative max-w-7xl mx-auto px-4 pt-12 pb-12 lg:pt-20 lg:pb-16 flex flex-col lg:flex-row items-center justify-between">
          {/* Texto principal */}
          <div className="lg:w-3/5 text-left mb-12 lg:mb-0">
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-3xl md:text-5xl lg:text-6xl font-extrabold text-[#004691] dark:text-[#5B8BD9] mb-6 leading-tight max-w-xl"
            >
              Sistema Inteligente de Gestión del Conocimiento
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="text-base md:text-lg text-slate-600 dark:text-slate-300 max-w-lg mb-6"
            >
              Corporación Ecuatoriana para el Desarrollo de la Investigación y la Academia.
              Gestión centralizada y acceso eficiente a la producción científica de las universidades miembro.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="flex gap-4"
            >
              <button
                onClick={() => navigate('/dashboards')}
                className="bg-[#004691] text-white px-6 md:px-8 py-3 rounded-md text-sm md:text-base font-semibold hover:bg-[#003366] transition-all shadow-md"
              >
                Explorar Dashboards
              </button>
            </motion.div>
          </div>

          {/* Bloque decorativo con logo CEDIA */}
          <div className="lg:w-2/5 hidden lg:flex justify-center">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative w-full max-w-md aspect-square bg-white/10 dark:bg-slate-900/40 backdrop-blur-sm border border-white/30 dark:border-slate-700/60 rounded-2xl flex items-center justify-center"
            >
              <img
                src="/images/cedia.webp"
                alt="CEDIA"
                className="w-1/2 opacity-50 object-contain"
              />
            </motion.div>
          </div>
        </section>

        {/* Grid de Universidades (4 tarjetas) */}
        <section className="relative z-10 max-w-7xl mx-auto px-4 pb-16">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {universidades.map((uni, idx) => (
              <motion.div
                key={uni.sigla}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.1 * idx }}
                whileHover={{ y: -4 }}
                className="bg-white dark:bg-slate-900 rounded-xl p-6 md:p-8 card-shadow border-t-4 border-[#00AEEF] flex flex-col items-center text-center border border-slate-100 dark:border-slate-800"
              >
                <button
                  type="button"
                  className="h-20 w-20 md:h-24 md:w-24 flex items-center justify-center mb-5"
                  onClick={() => openCarouselAt(idx)}
                  title={`Ver más de ${uni.sigla}`}
                >
                  <img
                    src={uni.logo}
                    alt={`Logo ${uni.sigla}`}
                    className="max-h-full max-w-full object-contain"
                  />
                </button>
                <h3 className="text-lg md:text-xl font-bold text-[#004691] mb-1">{uni.sigla}</h3>
                <p className="text-xs md:text-sm text-slate-500 dark:text-slate-400 mb-5 flex-grow">
                  {uni.nombre}
                </p>
                <a
                  className="w-full bg-[#004691] text-white py-2.5 rounded-md text-xs md:text-sm font-semibold hover:bg-[#003366] transition-colors"
                  href={uni.url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Ir al sitio web
                </a>
              </motion.div>
            ))}
          </div>
        </section>

        {/* KPIs (simulados) */}
        <section className="relative z-10 max-w-7xl mx-auto px-4 pb-20">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 text-center">
            <div className="flex flex-col items-center">
              <span className="text-4xl md:text-5xl font-extrabold text-[#004691] dark:text-[#5B8BD9] mb-1">
                <AnimatedNumber value={kpis.investigadores} />
              </span>
              <p className="text-[11px] md:text-xs text-slate-500 dark:text-slate-400 uppercase tracking-widest max-w-[220px]">
                total de investigadores cargados
              </p>
            </div>
            <div className="flex flex-col items-center">
              <span className="text-4xl md:text-5xl font-extrabold text-[#004691] dark:text-[#5B8BD9] mb-1">
                <AnimatedNumber value={kpis.publicaciones} />
              </span>
              <p className="text-[11px] md:text-xs text-slate-500 dark:text-slate-400 uppercase tracking-widest max-w-[220px]">
                publicaciones registradas en la base
              </p>
            </div>
            <div className="flex flex-col items-center">
              <span className="text-4xl md:text-5xl font-extrabold text-[#004691] dark:text-[#5B8BD9] mb-1">
                <AnimatedNumber value={kpis.articulos_q1} />
              </span>
              <p className="text-[11px] md:text-xs text-slate-500 dark:text-slate-400 uppercase tracking-widest max-w-[220px]">
                artículos Q1 registrados
              </p>
            </div>
          </div>
        </section>
      </main>

      {/* Footer simplificado (solo la línea de proyecto CEDIA) */}
      <footer className="bg-white dark:bg-slate-950 border-t border-slate-100 dark:border-slate-800 py-6">
        <div className="max-w-7xl mx-auto px-4">
          <p className="text-xs text-slate-400 dark:text-slate-500 text-center">
            Proyecto financiado por los Fondos I+D+i de CEDIA. © 2026 Todos los derechos reservados.
          </p>
        </div>
      </footer>

      {/* Modal Carousel (filmstrip) */}
      <AnimatePresence>
        {modalOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] flex items-center justify-center p-4"
            role="dialog"
            aria-modal="true"
          >
            <div className="absolute inset-0 bg-slate-900/60" onClick={() => setModalOpen(false)} />

            <motion.div
              initial={{ opacity: 0, y: 20, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 20, scale: 0.98 }}
              transition={{ duration: 0.18 }}
              className="relative w-full max-w-5xl bg-white dark:bg-slate-900 rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-800 overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 dark:border-slate-800">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center">
                    <span className="material-icons-round text-[#004691]">collections</span>
                  </div>
                  <div>
                    <p className="text-sm font-extrabold text-slate-900 dark:text-white">Universidades</p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">Usa ← → para navegar</p>
                  </div>
                </div>
                <button
                  className="p-2 rounded-lg text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"
                  onClick={() => setModalOpen(false)}
                  aria-label="Cerrar"
                >
                  <span className="material-icons-round">close</span>
                </button>
              </div>

              <div className="relative">
                {/* Arrows */}
                <button
                  className="hidden md:flex absolute left-3 top-1/2 -translate-y-1/2 z-10 items-center justify-center w-10 h-10 rounded-full bg-white/90 dark:bg-slate-900/90 border border-slate-200 dark:border-slate-700 shadow hover:bg-white dark:hover:bg-slate-900 disabled:opacity-40"
                  onClick={() => setActiveIndex((i) => Math.max(0, i - 1))}
                  disabled={activeIndex === 0}
                  aria-label="Anterior"
                >
                  <span className="material-icons-round text-slate-700 dark:text-slate-200">chevron_left</span>
                </button>
                <button
                  className="hidden md:flex absolute right-3 top-1/2 -translate-y-1/2 z-10 items-center justify-center w-10 h-10 rounded-full bg-white/90 dark:bg-slate-900/90 border border-slate-200 dark:border-slate-700 shadow hover:bg-white dark:hover:bg-slate-900 disabled:opacity-40"
                  onClick={() => setActiveIndex((i) => Math.min(universidades.length - 1, i + 1))}
                  disabled={activeIndex === universidades.length - 1}
                  aria-label="Siguiente"
                >
                  <span className="material-icons-round text-slate-700 dark:text-slate-200">chevron_right</span>
                </button>

                {/* Filmstrip */}
                <div
                  ref={stripRef}
                  className="flex gap-6 overflow-x-auto px-5 py-6 scroll-smooth snap-x snap-mandatory"
                >
                  {universidades.map((u, i) => (
                    <button
                      key={u.sigla}
                      type="button"
                      onClick={() => setActiveIndex(i)}
                      className={`relative snap-center shrink-0 w-[260px] sm:w-[320px] md:w-[360px] h-[360px] sm:h-[400px] rounded-2xl overflow-hidden transition-transform ${
                        i === activeIndex ? 'ring-4 ring-[#00AEEF] scale-[1.01]' : 'ring-1 ring-slate-200 hover:scale-[1.01]'
                      } bg-gradient-to-br from-slate-50 via-white to-blue-50 dark:from-slate-900 dark:via-slate-900 dark:to-slate-800 card-shadow`}
                    >
                      {/* "Imagen" grande + label abajo izquierda (filmstrip style) */}
                      <div className="absolute inset-0 p-8 flex items-center justify-center">
                        <img
                          src={u.logo}
                          alt={`Logo ${u.sigla}`}
                          className="max-h-[70%] max-w-[80%] object-contain drop-shadow-sm"
                        />
                      </div>

                      <div className="absolute inset-x-0 bottom-0 p-4">
                        <div className="inline-flex items-center gap-2 bg-white/95 dark:bg-slate-950/90 border border-slate-200 dark:border-slate-700 shadow-sm rounded-lg px-3 py-2">
                          <span className="font-semibold text-slate-800 dark:text-slate-100">{u.sigla}</span>
                        </div>
                        <div className="mt-3 flex gap-2">
                          <a
                            href={u.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="flex-1 bg-[#004691] text-white py-2.5 rounded-md text-sm font-semibold hover:bg-[#003366] transition-colors text-center"
                          >
                            Ir al sitio web
                          </a>
                          <button
                            type="button"
                            className="flex-1 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 py-2.5 rounded-md text-sm font-semibold hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
                            onClick={(e) => {
                              e.stopPropagation();
                              setModalOpen(false);
                              navigate('/dashboards');
                            }}
                          >
                            Dashboards
                          </button>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>

                {/* Dots */}
                <div className="flex items-center justify-center gap-2 pb-5">
                  {universidades.map((u, i) => (
                    <button
                      key={u.sigla}
                      onClick={() => setActiveIndex(i)}
                      className={`h-2.5 rounded-full transition-all ${
                        i === activeIndex ? 'w-7 bg-[#00AEEF]' : 'w-2.5 bg-slate-300'
                      }`}
                      aria-label={`Ir a ${u.sigla}`}
                    />
                  ))}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default Inicio;
